/**
 * What every part of the 3D scene builds with: the plan-to-world mapping, the
 * one rule for which way a plan ring's sides face, flat fills over plan
 * rings, and the solid box, plan rings extruded over a vertical span.
 */
import { type Point, ringSignedArea } from '@kaiizen/kai-floor-plan';
import { triangulatePolygon, type Vec3 } from '../math3d';
import { ALWAYS, type MeshBuilder, type RGB, type SceneSolidLayer } from './mesh3d';

/** Maps a plan point (x, y) at `height` into the scene's world frame. */
export type PlanToWorld = (x: number, y: number, height: number) => Vec3;

/**
 * Each side of a closed plan ring, from its start to its end, and the plan
 * direction it faces: out of the ring, or into it when `inward`. It reads the
 * ring's winding, so it holds for concave rings such as an outer perimeter
 * with alcove jogs. `facing` is not normalized, since a quad takes only its
 * sense.
 */
export function ringSides(ring: readonly Point[], inward = false): { start: Point; end: Point; facing: Vec3 }[] {
  const sign = ringSignedArea(ring) > 0 !== inward ? 1 : -1;
  return ring.map((start, index) => {
    const end = ring[(index + 1) % ring.length];
    return { start, end, facing: [sign * (end.y - start.y), 0, sign * (start.x - end.x)] };
  });
}

/** Fills plan rings (a footprint, holes after it) flat at `height`, facing up, or down when `facing` is -1. */
export function fillRings(mesh: MeshBuilder, rings: Point[][], height: number, color: RGB, toWorld: PlanToWorld, facing: 1 | -1 = 1): void {
  for (const triangle of triangulatePolygon(rings)) mesh.fan(triangle.map(point => toWorld(point.x, point.y, height)), [0, facing, 0], color);
}

/**
 * A solid box from plan rings (a footprint, holes after it) extruded over
 * [base, top]: sides facing away from the material (out of the footprint,
 * into each hole), a dark outline, the top cap, and a bottom cap when raised
 * (base > 0) so a floating box (a wall cabinet, an AC unit) reads solid from
 * below. The caps are triangulated, so chamfered corners and holes fill exactly.
 */
export function buildBox(
  mesh: MeshBuilder,
  rings: Point[][],
  base: number,
  top: number,
  color: RGB,
  toWorld: PlanToWorld,
  layer: SceneSolidLayer,
  group: number = ALWAYS,
  name?: string,
): void {
  if ((rings[0]?.length ?? 0) < 3 || top - base < 1e-3) return;
  mesh.solid({ layer, name, rings, closed: true, base, top });
  mesh.begin(group);
  rings.forEach((ring, index) => {
    for (const { start, end, facing } of ringSides(ring, index > 0)) {
      const bottomStart = toWorld(start.x, start.y, base), bottomEnd = toWorld(end.x, end.y, base);
      const topStart = toWorld(start.x, start.y, top), topEnd = toWorld(end.x, end.y, top);
      mesh.quad(bottomStart, bottomEnd, topEnd, topStart, facing, color);
      mesh.line(bottomStart, bottomEnd);
      mesh.line(topStart, topEnd);
      mesh.line(bottomStart, topStart);
    }
  });
  fillRings(mesh, rings, top, color, toWorld);
  if (base > 1e-3) fillRings(mesh, rings, base, color, toWorld, -1);
  mesh.end();
}
